import { Component, EventEmitter, Input, OnInit, Output, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { DropdownModule } from 'primeng/dropdown';
import { CheckboxModule } from 'primeng/checkbox';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';
import { FirebaseStorageService } from '../../../services/firebase-storage.service';
import { InterventiService } from '../../../services/interventi.service';
import { RichiesteService } from '../../../services/richieste.service';
import { Intervento } from '../../../models/intervento.model';
import { Richiesta } from '../../../models/richiesta.model';
import { DeterminaInterventoRequest } from '../../../models/requests/determina-intervento.model';
import { AllegatoIntervento } from '../../../models/allegato-intervento.model';

@Component({
  selector: 'app-assegna-massivamente-determine',
  templateUrl: './assegna-massivamente-determine.component.html',
  styleUrls: ['./assegna-massivamente-determine.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    FileUploadModule,
    DropdownModule,
    CheckboxModule,
    ToastModule
  ],
  providers: [MessageService]
})
export class AssegnaMassivamenteDetermineComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() richiestaPreselezionata: Richiesta | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() determineAssegnate = new EventEmitter<void>();

  // Step 1: Dati determina
  numeroDetermina: string = '';
  note: string = '';
  fileDetermina: File | null = null;

  // Step 2: Selezione interventi
  anniDisponibili: number[] = [];
  annoSelezionato: number | null = null;
  richiesteAnno: Richiesta[] = [];
  richiestaSelezionata: Richiesta | null = null;
  interventiRichiesta: Intervento[] = [];
  interventiSelezionati: Intervento[] = [];

  // Stato del componente
  currentStep: number = 1;
  isLoading: boolean = false;

  constructor(
    private messageService: MessageService,
    private logger: NGXLogger,
    private firebaseStorageService: FirebaseStorageService,
    private interventiService: InterventiService,
    private richiesteService: RichiesteService
  ) {}

  ngOnInit() {
    this.caricaAnniDisponibili();
    // Gestisci la preselezione se disponibile al primo caricamento
    if (this.richiestaPreselezionata) {
      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Richiesta preselezionata disponibile al primo caricamento:', this.richiestaPreselezionata);
      this.gestisciPreselezioneRichiesta();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    // Reagisce ai cambiamenti di richiestaPreselezionata
    if (changes['richiestaPreselezionata'] && !changes['richiestaPreselezionata'].firstChange) {
      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Richiesta preselezionata cambiata:', this.richiestaPreselezionata);
      this.gestisciPreselezioneRichiesta();
    }
  }

  show() {
    this.visible = true;
    this.visibleChange.emit(this.visible);
    this.resetForm();
    // Gestisci la preselezione dopo il reset
    this.gestisciPreselezioneRichiesta();
  }

  private gestisciPreselezioneRichiesta() {
    if (this.richiestaPreselezionata) {
      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Preselezionando richiesta:', this.richiestaPreselezionata);
      this.annoSelezionato = this.richiestaPreselezionata.anno;
      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Anno selezionato:', this.annoSelezionato);
      this.caricaRichiesteAnno();
    } else {
      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] NESSUNA RICHIESTA PRESELEZIONATA');
    }
  }

  hide() {
    this.visible = false;
    this.visibleChange.emit(this.visible);
    this.resetForm();
  }

  resetForm() {
    this.currentStep = 1;
    this.numeroDetermina = '';
    this.note = '';
    this.fileDetermina = null;
    this.annoSelezionato = null;
    this.richiestaSelezionata = null;
    this.richiesteAnno = [];
    this.interventiRichiesta = [];
    this.interventiSelezionati = [];
    this.isLoading = false;
  }

  // Step 1: Gestione file determina
  onFileSelect(event: any) {
    this.fileDetermina = event.files && event.files.length > 0 ? event.files[0] : null;
  }

  isStep1Valid(): boolean {
    // Evita il log se il componente non è visibile
    if (!this.visible) {
      return false;
    }

    const hasNumero = this.numeroDetermina.trim() !== '';
    const hasFile = this.fileDetermina !== null;

    return hasNumero && hasFile;
  }

  nextStep() {
    if (this.isStep1Valid()) {
      this.currentStep = 2;
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Inserisci il numero determina e seleziona almeno un file'
      });
    }
  }

  prevStep() {
    this.currentStep = 1;
  }

  // Step 2: Gestione selezione interventi
  caricaAnniDisponibili() {
    const currentYear = new Date().getFullYear();
    this.anniDisponibili = [];
    for (let year = currentYear; year >= 2020; year--) {
      this.anniDisponibili.push(year);
    }
  }

  onAnnoChange() {
    if (this.annoSelezionato) {
      this.caricaRichiesteAnno();
    }
    this.richiestaSelezionata = null;
    this.interventiRichiesta = [];
    this.interventiSelezionati = [];
  }

  caricaRichiesteAnno() {
    if (!this.annoSelezionato) return;

    this.isLoading = true;
    this.richiesteService.getRichiesteByAnno(this.annoSelezionato).subscribe({
      next: (richieste) => {
        this.logger.info(richieste);
        this.richiesteAnno = richieste.filter(richiesta => {
          const stato = richiesta.stato?.descrizione?.toUpperCase();
          return stato === 'IN RENDICONTAZIONE' || stato === 'RENDICONTATO';
        });
        //this.richiesteAnno = richieste;
        // Filtra solo le richieste con stati "RENDICONTATO PARZIALMENTE" o "RENDICONTATO COMPLETAMENTE"

        this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Richieste filtrate caricate:', this.richiesteAnno);

        // Se c'è una richiesta preselezionata, selezionala automaticamente
        if (this.richiestaPreselezionata) {
          this.richiestaSelezionata = this.richiesteAnno.find(r => r.id === this.richiestaPreselezionata!.id) || null;
          if (this.richiestaSelezionata) {
            this.caricaInterventiRichiesta();
          }
        }

        this.isLoading = false;
      },
      error: (error) => {
        this.logger.error('[ASSEGNA MASSIVAMENTE DETERMINE] Errore nel caricamento richieste:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel caricamento delle richieste'
        });
        this.isLoading = false;
      }
    });
  }

  onRichiestaChange() {
    if (this.richiestaSelezionata) {
      this.caricaInterventiRichiesta();
    }
    this.interventiRichiesta = [];
    this.interventiSelezionati = [];
  }

  caricaInterventiRichiesta() {
    if (!this.richiestaSelezionata) return;

    this.isLoading = true;
    this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Caricamento interventi per richiesta:', this.richiestaSelezionata.id);
    this.interventiService.getInterventiByRichiesta(this.richiestaSelezionata.id).subscribe({
      next: (interventi) => {
        this.interventiRichiesta = interventi.filter(intervento => {
          const stato = intervento.stato?.descrizione?.toUpperCase();
          return stato === 'RENDICONTATO PARZIALMENTE' || stato === 'RENDICONTATO COMPLETAMENTE';
        });
        this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Interventi caricati:', this.interventiRichiesta.length);
        this.isLoading = false;
      },
      error: (error) => {
        this.logger.error('[ASSEGNA MASSIVAMENTE DETERMINE] Errore nel caricamento interventi:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore nel caricamento degli interventi'
        });
        this.isLoading = false;
      }
    });
  }

  onInterventoSelect(intervento: Intervento, isSelected: boolean) {
    if (isSelected) {
      if (!this.interventiSelezionati.find(i => i.id === intervento.id)) {
        this.interventiSelezionati.push(intervento);
      }
    } else {
      this.interventiSelezionati = this.interventiSelezionati.filter(i => i.id !== intervento.id);
    }
  }

  isInterventoSelected(intervento: Intervento): boolean {
    return this.interventiSelezionati.some(i => i.id === intervento.id);
  }

  selectAllInterventi() {
    this.interventiSelezionati = [...this.interventiRichiesta];
  }

  deselectAllInterventi() {
    this.interventiSelezionati = [];
  }

  isStep2Valid(): boolean {
    return this.annoSelezionato !== null &&
           this.richiestaSelezionata !== null &&
           this.interventiSelezionati.length > 0;
  }

  // Salvataggio finale
  async salvaDetermine() {
    if (!this.isStep1Valid() || !this.isStep2Valid()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Attenzione',
        detail: 'Completa tutti i campi obbligatori'
      });
      return;
    }

    this.isLoading = true;

    try {
      // Assegnazione determina a tutti gli interventi selezionati
      const promises = this.interventiSelezionati.map(async (intervento) => {
        // Upload del file determina per ogni intervento
        if (this.fileDetermina) {
          const rendicontazioni = await this.interventiService.getRendicontazioniByInterventoSenzaDetermina(intervento.id!).toPromise();
          if (rendicontazioni && rendicontazioni.length > 0) {
            for (const rendicontazione of rendicontazioni) {
              const path = `interventi/${intervento.id}/determine-umvs/${rendicontazione.id}/${this.fileDetermina.name}`;
              await this.firebaseStorageService.uploadFile(this.fileDetermina, path).toPromise();

              // Crea la request per comunicaDetermina
              const request: DeterminaInterventoRequest = {
                numeroDetermina: this.numeroDetermina,
                note: this.note,
                allegati: [{
                  id: null,
                  nomeFile: this.fileDetermina.name,
                  percorsoFile: path,
                  tipoFile: this.fileDetermina.type,
                  dimensioneKb: this.fileDetermina.size
                }]
              };

              // Chiama comunicaDetermina per questa rendicontazione
              await this.interventiService.comunicaDetermina(intervento.id!, rendicontazione.id!, request).toPromise();
            }
          }
        }

        return Promise.resolve();
      });

      await Promise.all(promises);

      this.logger.info('[ASSEGNA MASSIVAMENTE DETERMINE] Determine assegnate con successo a', this.interventiSelezionati.length, 'interventi');

      this.messageService.add({
        severity: 'success',
        summary: 'Successo',
        detail: `Determina assegnata con successo a ${this.interventiSelezionati.length} interventi`
      });

      this.determineAssegnate.emit();
      this.hide();

    } catch (error) {
      this.logger.error('[ASSEGNA MASSIVAMENTE DETERMINE] Errore nell\'assegnazione determine:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Errore nell\'assegnazione delle determine'
      });
    } finally {
      this.isLoading = false;
    }
  }
}
